import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  FiSettings,
  FiCheck,
  FiArrowRight,
} from 'react-icons/fi';

// Hooks
import { useVisaApplication } from '../hooks/useVisaApplication';

// Components
import { PageContainer, MainCard, CardSection, ActionSection, FooterSection } from './Layout';
import { StyledButton } from './Button';
import { CountrySelector } from './CountrySelector';
import { DateSelector } from './DateSelector';
import { DurationSelector } from './DurationSelector';
import { OnboardingGuide } from './OnboardingGuide';
import UserPreferencesPopup from './UserPreferencesPopup';
import { IntegratedTimeline } from './IntegratedTimeline';
import { TimeAccuracyInfo } from './TimeAccuracyInfo';

// New Components
import { VisaFormHeader } from './VisaFormHeader';

// Utils
import { theme } from '../styles/theme';

// Styled Components for Layout
const MobileOptimizedContainer = styled.div`
  /* Mobile-First Header styles are now in VisaFormHeader */

  .mobile-step-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 0.75rem;
    
    ${({ theme }) => theme.media.mobileLarge} {
      gap: 1.25rem;
      padding: 0 1rem;
    }
    
    ${({ theme }) => theme.media.tablet} {
      gap: 1.5rem;
      padding: 0;
    }
  }

  .mobile-touch-target {
    min-height: ${({ theme }) => theme.mobile.touchTarget};
    padding: 1rem;
    
    ${({ theme }) => theme.media.mobileLarge} {
      min-height: ${({ theme }) => theme.mobile.touchTargetLarge};
      padding: 1.25rem;
    }
  }

  /* Compact Mobile Layout for Form Elements */
  .mobile-form-element {
    width: 100%;
    
    @media (max-width: 375px) {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
  }
`;

// Footer Styled Components
const ContactSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const FooterIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs || '8px'};
`;

const ContactText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    text-decoration: underline;
  }
`;

// Progress Overlay Components
const ProgressOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
`;

const ProgressContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 40px;
  box-shadow: 0 25px 50px ${({ theme }) => theme.colors.background.overlay};
  border: 2px solid ${({ theme }) => theme.colors.success};
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ProgressTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 20px 0;
`;

const ProgressText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin: 0 0 30px 0;
  line-height: 1.5;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background: ${({ theme }) => theme.colors.gradient.primary};
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadows.glow};
`;

const ProgressPercentage = styled(motion.div)`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const Spinner = styled(motion.div)`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.success};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  margin: 0 auto 20px;
`;

const ResetMainButton = styled(motion.button)`
  width: 100%;
  max-width: 300px;
  padding: 14px ${({ theme }) => theme.spacing.xl};
  font-size: 14px;
  font-weight: 700;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.error};
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 12px;
  
  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.05);
    border-color: ${({ theme }) => theme.colors.text.error};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    background: rgba(239, 68, 68, 0.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    filter: grayscale(1);
  }
`;

const CheckStatusButton = styled(motion.button)`
  width: 100%;
  max-width: 300px;
  padding: 16px ${({ theme }) => theme.spacing.xl};
  font-size: 16px;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradient.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: ${({ theme }) => theme.spacing.lg} 0 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    filter: brightness(1.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    filter: brightness(0.9);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: saturate(0.5);
  }
`;

const StepperContainer = styled.div`
  margin: ${({ theme }) => theme.spacing.lg} 0;
  padding: 0 ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 480px) {
    margin: ${({ theme }) => theme.spacing.md} 0;
  }
`;

const StepTitleContainer = styled(motion.div)`
  text-align: center;
  margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.lg} 0;
  padding: 0 ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 480px) {
    margin: ${({ theme }) => theme.spacing.sm} 0 ${({ theme }) => theme.spacing.md} 0;
  }
`;

const StepTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  letter-spacing: 0.8px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0;
  text-transform: uppercase;
`;

export const VisaForm = (props) => {
  const {
    selectedDate,
    visaDuration,
    selectedCountry,
    onDateChange,
    onDurationChange,
    onCountryChange,
    timeAccuracy
  } = props;

  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const { state, actions } = useVisaApplication({
    ...props,
    showOnboarding
  });

  React.useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
      // Small delay to show after initial render
      const timer = setTimeout(() => setShowOnboarding(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
    // Preferences can be accessed via the Settings button in the header
  };

  // Refs for auto-scrolling
  const dateRef = React.useRef(null);
  const durationRef = React.useRef(null);
  const confirmRef = React.useRef(null);

  // Auto-scroll logic
  React.useEffect(() => {
    if (selectedCountry && !selectedDate && dateRef.current) {
      setTimeout(() => {
        dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [selectedCountry, selectedDate]);

  React.useEffect(() => {
    if (selectedCountry && selectedDate && !visaDuration && durationRef.current) {
      setTimeout(() => {
        durationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [selectedCountry, selectedDate, visaDuration]);

  React.useEffect(() => {
    if (selectedCountry && selectedDate && visaDuration && confirmRef.current) {
      setTimeout(() => {
        confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [selectedCountry, selectedDate, visaDuration]);

  return (
    <MobileOptimizedContainer>
      <PageContainer>
        <MainCard
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 100 }}
        >
          {/* Header Component - Provides IP localization and Settings/Language */}
          <VisaFormHeader
            location={state.location}
            actions={actions}
            language={state.language}
            setLanguage={actions.setLanguage}
          />

          {/* Step Progress Indicator */}
          <StepperContainer>
            <IntegratedTimeline
              currentStep={state.currentStep}
              completedSteps={state.completedSteps}
            />
          </StepperContainer>

          {/* Country Selection Section */}
          <CardSection>
            <CountrySelector
              selectedCountry={selectedCountry}
              onCountryChange={onCountryChange}
              currentStep={state.currentStep}
              completedSteps={state.completedSteps}
              externalOpen={state.showCountryPopup}
              onPopupClose={actions.handleCountryPopupClose}
              location={state.location}
            />
          </CardSection>

          {/* Nested Selection Flow */}
          {selectedCountry && (
            <CardSection>
              <div>
                <div ref={dateRef}>
                  <DateSelector
                    selectedDate={selectedDate}
                    onDateChange={onDateChange}
                    currentStep={state.currentStep}
                    completedSteps={state.completedSteps}
                    externalOpen={state.showCalendar}
                    onPopupClose={actions.handleCalendarClose}
                  />
                </div>

                {selectedCountry && selectedDate && (
                  <div ref={durationRef} style={{ marginTop: '24px' }}>
                    <DurationSelector
                      visaDuration={visaDuration}
                      onDurationChange={onDurationChange}
                      currentStep={state.currentStep}
                      completedSteps={state.completedSteps}
                      externalOpen={state.showDurationPopup}
                      onPopupClose={actions.handleDurationPopupClose}
                    />
                  </div>
                )}

                {selectedCountry && selectedDate && visaDuration && (
                  <div ref={confirmRef}>
                    <ActionSection $noBorder style={{ marginTop: '24px' }}>
                      <CheckStatusButton
                        onClick={actions.handleConfirmVisaCheck}
                        disabled={state.isProcessing}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {state.isProcessing ? 'Processing...' : (
                          <>CHECK VISA STATUS <FiArrowRight /></>
                        )}
                      </CheckStatusButton>
                      <ResetMainButton
                        onClick={props.onReset}
                        disabled={state.isProcessing}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        RESET
                      </ResetMainButton>
                    </ActionSection>
                  </div>
                )}
              </div>
            </CardSection>
          )}

          {/* Footer Section */}
          <FooterSection
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <ContactSection>
              <FooterIconContainer>
                <TimeAccuracyInfo timeAccuracy={timeAccuracy} />
              </FooterIconContainer>
              <ContactText>Version: 2.4.1 | For Tourists Worldwide</ContactText>
              <ContactText>
                Need help? Contact{' '}
                <ContactLink href="mailto:info.mindav@protonmail.com">
                  info.mindav@protonmail.com
                </ContactLink>
              </ContactText>
            </ContactSection>
          </FooterSection>
        </MainCard>

        {/* Global Modals & Popups */}
        <UserPreferencesPopup
          isOpen={state.showPreferencesPopup}
          onClose={actions.handlePreferencesClose}
          onConfirm={actions.handlePreferencesConfirm}
        />

        <OnboardingGuide
          isOpen={showOnboarding}
          onClose={handleCloseOnboarding}
          onOpenPreferences={actions.handleOpenPreferences}
        />

        {/* Premium Progress Overlay */}
        <AnimatePresence>
          {state.isProcessing && (
            <ProgressOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ProgressContainer
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
              >
                <Spinner
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <ProgressTitle>
                  {state.processingStep === 'validating' && 'Validating Details'}
                  {state.processingStep === 'calculating' && 'Calculating Duration'}
                  {state.processingStep === 'finalizing' && 'Finalizing Report'}
                  {!state.processingStep && 'Processing...'}
                </ProgressTitle>
                <ProgressText>
                  {state.processingStep === 'validating' && 'Checking country specific rules and regulations...'}
                  {state.processingStep === 'calculating' && 'Adjusting for timezones and local holidays...'}
                  {state.processingStep === 'finalizing' && 'Preparing your personalized visa status...'}
                  {!state.processingStep && 'Please wait while we process your request...'}
                </ProgressText>
                <ProgressBarContainer>
                  <ProgressBar
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 3, ease: "linear" }}
                  />
                </ProgressBarContainer>
                <ProgressPercentage
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  {state.processingStep === 'validating' && '33%'}
                  {state.processingStep === 'calculating' && '66%'}
                  {state.processingStep === 'finalizing' && '99%'}
                </ProgressPercentage>
              </ProgressContainer>
            </ProgressOverlay>
          )}
        </AnimatePresence>
      </PageContainer>
    </MobileOptimizedContainer>
  );
};